import DataTable, { Column } from '@/components/CommonComponents/DataTable';
import Pagination from '@/components/CommonComponents/Pagination';
import StatCard from '@/components/CommonComponents/StatCard';
import ViewDetailsModal from '@/components/CommonComponents/ViewDetailsModal';
import { showToast } from '@/helper/toast.helper';
import { desanitizeKey, formatCurrency } from '@/helper/utils';
import { useDebounce } from '@/hooks/useDebounce';
import { useExportFile } from '@/hooks/useExportFile';
import { usePermission } from '@/hooks/usePermission';
import { surveySetManagementService } from '@/services/surveySetManagementService/surveySetManagementService';
import { AppDispatch, RootState } from '@/store';
import {
  deleteSurveySet,
  getAllSurveySets,
  getSurveySetsStats,
  removeSurveySetFromList,
  setFilters,
  setPage,
  setPageSize,
  updateSurveySetInList,
  updateSurveySetStatus,
} from '@/store/slices/surveySetManagementSlice/surveySetManagementSlice';
import { RowPermissions, SurveySetListItem } from '@/types/surveySet.types';
import { Eye, MessagesSquare, Paperclip } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import DeleteModal from '../CommonComponents/DeleteModal';
import ServerSearchFilter from '../CommonComponents/ServerSearchFilter';
import PageHeaderWrapper from '../Layout/PageHeaderWrapper';
import { createAuthorizer } from '../authguards/casbin/enforcer';

type SurveySet = {
  id: string;
  title: string;
  languages: string;
  code: string;
  items: number;
  project: string;
  status: boolean;
  createdOn: string;
  createdBy?: string;
  start: string;
  end: string;
  rule: string;
  rewardAmount: number | string;
  rewardCurrency?: string;
  claimType: string;
  _permissions?: RowPermissions;
  site?: {
    id: number;
    project: {
      id: number;
    };
  };
};

const mapSurveySetListItemToSurveySet = (survey: SurveySetListItem): SurveySet => ({
  id: survey.id.toString(),
  title: survey.title,
  code: survey.posterCode.toString(),
  items: survey.numberOfSurveys,
  project: survey.site.project.name,
  status: survey.status,
  createdOn: new Date(survey.createdAt).toLocaleDateString('en-CA'),
  createdBy: survey.createdBy ?? 'N/A',
  start: survey.startDate
    ? new Date(survey.startDate).toLocaleDateString('en-CA', { timeZone: 'UTC' })
    : 'N/A',
  end: survey.endDate
    ? new Date(survey.endDate).toLocaleDateString('en-CA', { timeZone: 'UTC' })
    : 'N/A',
  languages: (survey.languages?.length ? survey.languages : survey.site.languages)
    .map((lang) => lang.name)
    .join(', '),
  rule: survey.submissionRule,
  rewardAmount: survey.rewardAmount,
  rewardCurrency: survey.currency,
  claimType: survey.claimType
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (l) => l.toUpperCase()),
});

const columns: Column<SurveySet>[] = [
  { key: 'id', header: 'Survey-Set ID' },
  { key: 'title', header: 'Survey-Set Title' },
  { key: 'code', header: 'Poster-Code' },
  { key: 'items', header: 'Surveys' },
  { key: 'project', header: 'Linked Project' },
  { key: 'createdOn', header: 'Created On' },
  { key: 'start', header: 'Start Date' },
  { key: 'end', header: 'End Date' },
  {
    key: 'rule',
    header: 'Submission Rule',
    render: (value: string, _row) => {
      return desanitizeKey(value);
    },
  },
  { key: 'languages', header: 'Languages' },
  // { key: 'rewardCurrency', header: 'Reward Currency' },
  {
    key: 'rewardAmount',
    header: 'Total Rewards',
    render: (value, _row) => {
      const val =
        formatCurrency(value, _row.rewardCurrency!) +
        ' (' +
        _row.rewardCurrency?.toUpperCase() +
        ')';
      return val;
    },
  },
  { key: 'claimType', header: 'Claim Type' },
  {
    key: 'status',
    header: 'Status',
  },
];

interface SurveySetDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  surveySetDetails: SurveySetListItem | null;
}

const SurveySetManagement: React.FC = () => {
  const [filteredSurveys, setFilteredSurveys] = useState<SurveySet[]>([]);

  // const [page, setPage] = useState(1);
  // const [pageSize, setPageSize] = useState(10);

  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  const [previewSurvey, setPreviewSurvey] = useState<SurveySet>();
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const [selectedSurveyForView, setSelectedSurveyForView] = useState<SurveySetListItem | null>(
    null,
  );

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [surveySetToDelete, setSurveySetToDelete] = useState<SurveySet | null>(null);

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();

  const { list, stats, loading } = useSelector((state: RootState) => state.surveySet);
  const { page, pageSize, filters } = useSelector((state: RootState) => state.surveySet);

  const { exportFile } = useExportFile();

  const surveysData = list.data;
  // const { pageNumber } = useParams();

  // Get project ID from navigation state
  const incomingProjectId = location.state?.projectId;

  const debouncedFilters = useDebounce(filters, 500);
  const permissions = {
    create: usePermission('write', 'surveySets'),
    edit: usePermission('edit', 'surveySets'),
    delete: usePermission('delete', 'surveySets'),
    export: usePermission('export', 'surveySets'),
  };

  const handleAddSurveySet = () => navigate('/dashboard/surveyset-management/create');

  const handleExport = () => {
    exportFile(() => surveySetManagementService.exportSurveySets(), `surveysets_${Date.now()}.csv`);
  };

  const handleViewSurveySet = (surveySet: SurveySet) => {
    const originalSurvey = surveysData.find((s) => s.id.toString() === surveySet.id);
    if (!originalSurvey) return;
    setSelectedSurveyForView(originalSurvey);
    setIsDetailsModalOpen(true);
  };

  const openDeleteModal = (surveySet: SurveySet) => {
    if (surveySet.status === true) {
      showToast.error('Cannot delete this survey-set. This survey-set is live now.');
      return;
    }
    setSurveySetToDelete(surveySet);
    setIsDeleteModalOpen(true);
  };

  const handleEditSurveySet = (row: SurveySet) => {
    const projectItem = surveysData.find((item) => item.site?.project?.name === row.project);
    const projectId = projectItem?.site?.project?.id;
    if (projectId) {
      localStorage.setItem('selectedProjectId', projectId.toString());
    }
    navigate('/dashboard/surveyset-management/edit', {
      state: { surveySetId: row.id },
    });
  };

  const handleDeleteSurveySet = async () => {
    const projectItem = surveysData.find(
      (item) => item.site?.project?.name === surveySetToDelete?.project,
    );
    const projectId = projectItem?.site?.project?.id;
    if (projectId) {
      localStorage.setItem('selectedProjectId', projectId.toString());
    }

    if (!surveySetToDelete) return;
    const surveySetId = parseInt(surveySetToDelete.id);
    dispatch(removeSurveySetFromList(surveySetId));
    setIsDeleteModalOpen(false);
    setSurveySetToDelete(null);

    try {
      const userId = 1;
      await dispatch(
        deleteSurveySet({
          id: surveySetToDelete.id,
          data: userId,
        }),
      ).unwrap();

      // showToast.success('Survey-set deleted successfully!');

      dispatch(getSurveySetsStats());
    } catch (error) {
      console.error('Error deleting survey set:', error);
      showToast.error(error as string);
      dispatch(getAllSurveySets({ page, limit: pageSize }));
    }
  };

  const handleStatusToggle = async (id: string, newStatus: boolean) => {
    const selected = surveysData.find((s) => s.id.toString() === id);
    if (!selected) return;

    const isLive = (() => {
      if (!selected.startDate || !selected.endDate) return false;

      const now = new Date();
      const start = new Date(selected.startDate);
      const end = new Date(selected.endDate);

      return selected.status === true && now >= start && now <= end;
    })();

    dispatch(
      updateSurveySetInList({
        id: selected.id,
        updates: { status: newStatus },
      }),
    );

    try {
      await dispatch(
        updateSurveySetStatus({
          id,
          data: {
            status: newStatus,
            claimType: selected.claimType,
          },
        }),
      ).unwrap();

      dispatch(getSurveySetsStats());
    } catch (error) {
      dispatch(
        updateSurveySetInList({
          id: selected.id,
          updates: { status: !newStatus },
        }),
      );
      showToast.error(error as string);
    }
  };

  const handlePreview = (surveySet: SurveySet) => {
    setPreviewSurvey(surveySet);
    setIsPreviewOpen(true);
    navigate('/dashboard/surveyset-management/selected-surveyset', {
      state: { surveySetId: surveySet.id },
    });
  };

  const getRowPermissions = async (survey: SurveySetListItem): Promise<RowPermissions> => {
    const projectId = survey.site.project.id.toString();

    const projectRoles = JSON.parse(localStorage.getItem('projectRoles') || '{}');

    const role = projectRoles[projectId];

    // No role → no access
    if (!role) {
      return {
        edit: true,
        delete: true,
        preview: true,
        statusToggle: true,
      };
    }

    const authorizer = createAuthorizer(role);
    return {
      edit: await authorizer.can('edit', 'surveySets', projectId),
      delete: await authorizer.can('delete', 'surveySets', projectId),
      preview: await authorizer.can('read', 'surveySets', projectId),
      statusToggle: await authorizer.can('edit', 'surveySets', projectId),
    };
  };

  const handleFilterChange = (newFilters: Record<string, any>) => {
    let newStatus: boolean | undefined;

    switch (newFilters?.status) {
      case 'Inactive':
        newStatus = false;
        break;
      case 'Active':
        newStatus = true;
        break;
      default:
        newStatus = undefined;
    }

    const nextFilters = {
      search: newFilters?.search ?? '',
      status: newStatus,
    };

    // Check if filters actually changed
    const filtersChanged =
      filters.search !== nextFilters?.search || filters.status !== nextFilters?.status;

    if (filtersChanged) {
      dispatch(setFilters(nextFilters));
      dispatch(setPage(1));
      dispatch(setPageSize(10));
    }
  };

  useEffect(() => {
    dispatch(getSurveySetsStats());
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      getAllSurveySets({
        page,
        limit: pageSize,
        search: debouncedFilters?.search,
        status: debouncedFilters?.status,
        projectId: incomingProjectId,
      }),
    );
  }, [dispatch, page, pageSize, debouncedFilters, incomingProjectId]);
  useEffect(() => {
    let cancelled = false;

    const buildRows = async () => {
      if (!surveysData || surveysData.length === 0) {
        setFilteredSurveys([]);
        return;
      }

      const rows: SurveySet[] = await Promise.all(
        surveysData.map(async (survey) => {
          const permissions = await getRowPermissions(survey);

          return {
            ...mapSurveySetListItemToSurveySet(survey),
            _permissions: permissions,
          };
        }),
      );

      if (!cancelled) {
        setFilteredSurveys(rows);
      }
    };

    buildRows();

    return () => {
      cancelled = true;
    };
  }, [surveysData]);

  useEffect(() => {
    if (surveysData && surveysData.length > 0) {
      setFilteredSurveys(surveysData.map(mapSurveySetListItemToSurveySet));
    } else {
      setFilteredSurveys([]);
    }
  }, [surveysData]);

  const surveyStats = useMemo(() => {
    return [
      {
        id: 'total',
        title: 'Total Survey-Sets',
        value: stats?.total.toString() || 0,
        icon: Paperclip,
        iconBg: 'bg-[#E79C4CCC]',
      },
      {
        id: 'inactive',
        title: 'Inactive Survey-Sets',
        value: stats?.inactive.toString() || 0,
        icon: MessagesSquare,
        iconBg: 'bg-[#F0DF4ACC]',
      },
      {
        id: 'active',
        title: 'Active Survey-Sets',
        value: stats?.active.toString() || 0,
        icon: Eye,
        iconBg: 'bg-[#0BA96F80]',
      },
    ];
  }, [stats]);

  return (
    <PageHeaderWrapper
      title="Survey-Set Management"
      description="Manage and monitor survey-sets across all projects"
      onAdd={handleAddSurveySet}
      onExport={handleExport}
      addLabel="Add Survey-Set"
      exportLabel="Export"
      permissions={permissions}
    >
      <div className="flex-shrink-0 mb-4">
        <ServerSearchFilter
          fields={[
            {
              type: 'search',
              key: 'search',
              placeholder: 'Search items...',
            },
            {
              type: 'radio-select',
              key: 'status',
              label: 'Select',
              options: ['Active', 'Inactive'],
              defaultValue:
                filters.status === undefined
                  ? 'All Status'
                  : filters.status
                    ? 'Active'
                    : 'Inactive',
            },
          ]}
          onChange={handleFilterChange}
        />
      </div>

      <div className="flex-shrink-0 mb-4">
        <div className="gap-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3">
          {surveyStats.map((stat) => (
            <StatCard
              key={stat.id}
              title={stat.title}
              value={stat.value}
              icon={stat.icon}
              iconBg={stat.iconBg}
            />
          ))}
        </div>
      </div>

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <DataTable
          listTitle=""
          columns={columns}
          data={filteredSurveys}
          loading={loading}
          onView={handleViewSurveySet}
          onEdit={(row) => {
            if (!row._permissions?.edit) return;
            handleEditSurveySet(row);
          }}
          onPreview={handlePreview}
          onDelete={(row) => {
            if (!row._permissions?.delete) return;
            openDeleteModal(row);
          }}
          onStatusToggle={handleStatusToggle}
          // permissions={permissions}
        />

        <Pagination
          currentPage={page}
          pageSize={pageSize}
          totalPages={list.totalPages || 1}
          onPageChange={(newPage) => dispatch(setPage(newPage))}
          onPageSizeChange={(size) => {
            dispatch(setPageSize(size));
            dispatch(setPage(1)); // reset page to 1 if pageSize changes
          }}
        />
      </div>
      <ViewDetailsModal
        title="Survey-Set Details"
        isOpen={isDetailsModalOpen}
        onClose={() => {
          setIsDetailsModalOpen(false);
          setSelectedSurveyForView(null);
        }}
        data={selectedSurveyForView!}
        fields={[
          {
            label: 'Survey-Set ID',
            value: selectedSurveyForView?.id.toString()!,
          },
          {
            label: 'Survey-Set Title',
            value: selectedSurveyForView?.title!,
          },
          {
            label: 'How Many Surveys',
            value: selectedSurveyForView?.numberOfSurveys.toString()!,
          },
          {
            label: 'Available Languages',
            value: selectedSurveyForView
              ? (selectedSurveyForView.languages?.length
                  ? selectedSurveyForView.languages
                  : selectedSurveyForView.site.languages
                ).map((l) => l.name)
              : ['N/A'],
          },
          {
            label: 'Linked Project',
            value: selectedSurveyForView?.site.project.name!,
          },
          {
            label: 'Linked Site',
            value: selectedSurveyForView?.site.name!,
          },
          {
            label: 'Poster Code',
            value: selectedSurveyForView?.posterCode.toString()!,
          },
          {
            label: 'Currency Type',
            value: selectedSurveyForView?.currency?.toUpperCase() ?? '',
          },
          {
            label: 'Reward Amount',
            value:
              formatCurrency(
                selectedSurveyForView?.rewardAmount ?? 0,
                selectedSurveyForView?.currency ?? 'USD',
              ) + ` (${selectedSurveyForView?.currency?.toUpperCase()})`,
          },
          {
            label: 'Claim Reward Type',
            value: desanitizeKey(selectedSurveyForView?.claimType!),
            list: true,
          },
          {
            label: 'Participants Limit',
            value: 'N/A',
          },
          {
            label: 'Status',
            value: selectedSurveyForView?.status ? 'Active' : 'Inactive',
            list: true,
          },
          {
            label: 'Start Date',
            value: new Date(selectedSurveyForView?.startDate!).toLocaleDateString('en-CA'),
          },
          {
            label: 'End Date',
            value: new Date(selectedSurveyForView?.endDate!).toLocaleDateString('en-CA'),
          },
          {
            label: 'Submission Rule',
            value: desanitizeKey(selectedSurveyForView?.submissionRule!),
            list: true,
          },
        ]}
      />

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSurveySetToDelete(null);
        }}
        onDelete={handleDeleteSurveySet}
        pageTitle="Survey-Set"
        itemName={surveySetToDelete?.title}
      />
    </PageHeaderWrapper>
  );
};

export default SurveySetManagement;
